import {EditorState, type Extension, StateField, Transaction} from '@codemirror/state'
import type {Nodes} from 'mdast'

import {getNodesContainingRange} from '../../language-tools/syntax-tree/node-range-utils'
import {setsEqual} from '../../utils'
import {getMarkdownTree, markdownParserExtension, markdownTreeChanged} from './markdown-parser'

export type SelectionContextValue = Nodes[]

export const initialSelectionContext: SelectionContextValue = []

/**
 * Determines the Markdown context of the current selection.
 * For example, if the selection is within bold text, the context will include a `strong` node.
 */
function getSelectionContext(state: EditorState): SelectionContextValue {
  const tree = getMarkdownTree(state)
  const selection = state.selection.main
  return getNodesContainingRange(tree, selection.from, selection.to, true)
}

/**
 * State field for accessing the current selection context. The context is updated synchronously
 * when the selection changes.
 */
const selectionContextField = StateField.define<SelectionContextValue>({
  create: getSelectionContext,
  update: (prev, tr) => {
    if (!tr.startState.selection.eq(tr.newSelection) || markdownTreeChanged(tr)) {
      return getSelectionContext(tr.state)
    }
    return prev
  },
  provide: () => [markdownParserExtension],
})

/**
 * Extension to track selection context that updates as the selection changes.
 */
export const selectionContextExtension: Extension = selectionContextField

/** Get the selection context from the editor state. Throws an error if `selectionContextExtension` is not installed. */
export function getCurrentSelectionContext(state: EditorState): SelectionContextValue {
  return state.field(selectionContextField)
}

/**
 * Check if the selection context changed in this transaction.
 */
export function selectionContextChanged(transaction: Transaction): boolean {
  const oldContext = getNodeTypes(transaction.startState.field(selectionContextField))
  const newContext = getNodeTypes(transaction.state.field(selectionContextField))

  return !setsEqual(oldContext, newContext)
}

/** Get the set of node types in the selection context. */
export function getNodeTypes(context: SelectionContextValue): Set<string> {
  const types = new Set<string>()
  for (const node of context) {
    types.add(node.type)
  }
  return types
}
